// Converts plain text to HTML with paragraph semantics.
// - Empty line(s) split paragraphs -> wrapped in <p>…</p>
// - Non-empty consecutive lines inside a paragraph are joined by <br>
// - Escapes HTML, optional linkify, optional preservation of multi-spaces
export function plaintextToHtml(
	input: string,
	opts: {
		linkify?: boolean;
		preserveSpaces?: boolean;
	} = {}
): string {
	const { linkify = true, preserveSpaces = true } = opts;

	// Normalize line endings
	const normalized = input.replace(/\r\n?|\r/g, "\n");

	// Split into paragraphs by one or more empty lines (lines with only whitespace count as empty)
	// This collapses multiple blank lines into a single paragraph break.
	const rawParagraphs = normalized
		.split(/\n(?:\s*\n)+/g)
		.map(p => p.replace(/^\s+|\s+$/g, "")) // trim outer whitespace per paragraph
		.filter(p => p.length > 0);            // drop fully empty paras

	// Escape HTML special characters
	const escapeHtml = (s: string) =>
		s
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");

	const linkifyUrls = (s: string) =>
		s.replace(
			/\bhttps?:\/\/[^\s<>"')]+/g,
			(url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
		);

	const preserveMultiSpaces = (s: string) =>
		// Replace runs of >=2 spaces so visual alignment is preserved.
		s.replace(/ {2,}/g, (m) => "&nbsp;".repeat(m.length - 1) + " ");

	// Render paragraphs
	const htmlParas = rawParagraphs.map(par => {
		// Work per paragraph: first escape, then optional linkify, then optional spaces, then <br> for single newlines
		let body = escapeHtml(par);
		if (linkify) body = linkifyUrls(body);
		if (preserveSpaces) body = preserveMultiSpaces(body);
		body = body.replace(/\n/g, "<br>");
		return `<p>${body}</p>`;
	});

	return htmlParas.join("");
}

/* ---- Example (matches your spec) ----
const txt = `AAAA
BBBB

CCCC

DDDD
EEE`;

console.log(plaintextToHtml(txt));
// -> <p>AAAA<br>BBBB</p><p>CCCC</p><p>DDDD<br>EEE</p>
*/